import { Component, HostBinding, ViewChild, ViewEncapsulation } from '@angular/core';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { HelpIndex, Endpoint, SettingsType } from '../../../../models/enums';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { NgForm } from '@angular/forms';
import { Survivorship } from '../../../../models/survivorship';
import { XMLService } from '../../../../services/xml/xml.service';
import { KeyValueListComponent } from '../../../key-value-list/key-value-list.component';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { DrNamedQuery } from '../../../../models/dr-named-query';

@Component({
    standalone: false,
    selector: 'res-survivorship',
    templateUrl: './survivorship.component.html',
    styleUrls: ['./survivorship.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class SurvivorshipComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChild(KeyValueListComponent, { static: true }) keyValueList: KeyValueListComponent;

    handle: string;
    isBusyMessage: string;
    validChanges = true;
    survivorshipModel: Survivorship[];
    helpIndex: HelpIndex;
    SearchTermMetricsFields = [];
    selectedDragObject: any;
    isBusy = false;
    survivorshipRows: Array<DrNamedQuery>;
    hasPermissions = true;
    isSystemUser = false;

    settingsType: SettingsType;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty || this.keyValueList.keyValueForm.form.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private xmlService: XMLService) { }

    selectedTreeItemChanged(treeItem: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.settingsType = settingsType;
        this.handle = projectHandle ? projectHandle : treeItem.data.handle;
        this.helpIndex = projectHandle ? HelpIndex.case_survivorship_manage_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_surv_model_dlg : HelpIndex.org_survivorship_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        const permissionType = projectHandle ? ObjectActionListType.PRJ_SURVIVORSHIP_SETTINGS : this.isSystemUser ? ObjectActionListType.SYSTEM_SURVIVORSHIP_MODEL_SETTINGS : ObjectActionListType.ORG_SURVIVORSHIP_SETTINGS;
        this.hasPermissions = Permissions.checkUserPermissions([{ permissionType: permissionType, permissionActions: [ActionType.CREATE] }]);
        this.loadSurvivorshipModelData();
    }

    loadSurvivorshipModelData(): void {
        this.survivorshipModel = [];
        this.survivorshipRows = [];

        this.isBusy = true;
        this.isBusyMessage = 'Loading Survivorship Model...';
        this.restService.post(Endpoint.projectManager_getDupeSurvivorshipSettings, { handle: this.handle, requestHandle: this.handle, systemScope: this.isSystemUser }).then(responseAllFields => {
            this.isBusy = false;
            if (responseAllFields && responseAllFields.requestHandle === this.handle) {
                if (responseAllFields.dupeSurvivorshipEntries && responseAllFields.dupeSurvivorshipEntries.length > 0) {
                    this.survivorshipModel = responseAllFields.dupeSurvivorshipEntries;
                    responseAllFields.dupeSurvivorshipEntries.forEach(element => {
                        const queryValue = (Object)(this.xmlService.xmlToQueryConverter(element.query, true)).query;
                        if (queryValue && queryValue.length > 1) {
                            this.survivorshipRows.push(new DrNamedQuery({ name: 'name', query: queryValue.substring(1, queryValue.length - 1), description: null, annotationText: null }));
                        }
                    });
                }
                if (this.hasPermissions) {
                    const emptyNamedQuery = new DrNamedQuery({ name: '', query: '', description: null, annotationText: null });
                    this.survivorshipRows.push(new DrNamedQuery(emptyNamedQuery));
                }
                this.survivorshipRows = [].concat(this.survivorshipRows);
            }
            this.isDirty = false;
        }, () => {
            this.isBusy = false;
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSurvivorshipModelData();
        this.keyValueList.keyValueForm.form.markAsPristine();
    }

    saveChanges(autoSaveChanges = false): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.isBusy = true;
            this.isBusyMessage = 'Validating Queries ...';

            this.keyValueList.validateQueries(this.keyValueList, this.survivorshipRows, true).then(isQueriesValid => {
                if (!isQueriesValid) {
                    // If queries validation fails, resolve with false and return without saving the survivorship queries
                    this.isBusy = false;
                    resolve(false);
                    return;
                }

                this.isBusy = true;
                this.isBusyMessage = 'Saving Survivorship Model...';
                const survivorshipElements = [];
                let index = 1;
                this.survivorshipRows.forEach(element => {
                    if (element.query !== '') {
                        const row = new Survivorship({
                            priority: index, executionOrder: index,
                            query: this.xmlService.stringToXMLQuery(element.query, true, true)
                        });
                        survivorshipElements.push(row);
                        index++;
                    }
                });
                this.restService.post(Endpoint.projectManager_setDupeSurvivorshipSettings, {
                    handle: this.handle,
                    dupeSurvivorshipEntries: survivorshipElements,
                    systemScope: this.isSystemUser
                }).then(() => {
                    this.isBusy = false;
                    if (!autoSaveChanges) {
                        this.loadSurvivorshipModelData();
                        this.keyValueList.keyValueForm.form.markAsPristine();
                    }
                    resolve(true);
                }, () => {
                    this.isBusy = false;
                    resolve(false);
                });
            }).catch(error => {
                this.isBusy = false;
                reject(error);
            })
        });
    }

    isSaveDisabled(): boolean {
        return (this.isBusy || !(this.isDirty || this.keyValueList.keyValueForm.form.dirty) || this.keyValueList.hasErrors());
    }

    isDiscardDisabled(): boolean {
        return (this.isBusy || !(this.isDirty || this.keyValueList.keyValueForm.form.dirty));
    }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);
}
